import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Modal,
  IconButton,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";

const LatestNews = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const fontFamily = isRTL ? "'Tajawal', Arial, sans-serif" : "Arial, Helvetica, sans-serif";

  const [open, setOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const scrollRef = useRef(null);
  const stateRef = useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleOpen = useCallback((card) => {
    setSelectedCard(card);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedCard(null);
  }, []);

  const handleMouseDown = useCallback((e) => {
    const { current } = scrollRef;
    stateRef.current.isDown = true;
    current.classList.add('active');
    stateRef.current.startX = e.pageX - current.offsetLeft;
    stateRef.current.scrollLeft = current.scrollLeft;
  }, []);

  const handleMouseLeave = useCallback(() => {
    stateRef.current.isDown = false;
    scrollRef.current.classList.remove('active');
  }, []);

  const handleMouseUp = useCallback(() => {
    stateRef.current.isDown = false;
    scrollRef.current.classList.remove('active');
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!stateRef.current.isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - stateRef.current.startX) * 1.5;
    scrollRef.current.scrollLeft = stateRef.current.scrollLeft - walk;
  }, []);

  // ✅ RTL-Compatible Auto Scroll
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationFrameId;
    const scrollSpeed = isMobile ? 1.2 : 3.5;
    let lastTimestamp = 0;
    let isPaused = false;

    const getRTLScrollType = () => {
      const el = document.createElement('div');
      el.style.width = '100px';
      el.style.height = '100px';
      el.style.overflow = 'scroll';
      el.style.direction = 'rtl';
      el.style.visibility = 'hidden';

      const inner = document.createElement('div');
      inner.style.width = '200px';
      el.appendChild(inner);

      document.body.appendChild(el);
      el.scrollLeft = 0;
      const initial = el.scrollLeft;

      el.scrollLeft = 1;
      const newVal = el.scrollLeft;

      document.body.removeChild(el);

      if (initial === 0 && newVal === 1) return 'positive'; // Chrome
      if (initial === 0 && newVal === 0) return 'negative'; // Firefox
      return 'unknown';
    };

    const rtlScrollType = isRTL ? getRTLScrollType() : null;

    const step = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const elapsed = timestamp - lastTimestamp;

      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const current = scrollContainer.scrollLeft;

      if (!isPaused) {
        if (isRTL) {
          if (rtlScrollType === 'negative') {
            if (current <= -maxScrollLeft + 1) scrollContainer.scrollLeft = 0;
            else scrollContainer.scrollLeft -= scrollSpeed * (elapsed / 16);
          } else {
            if (current <= 0) scrollContainer.scrollLeft = maxScrollLeft;
            else scrollContainer.scrollLeft -= scrollSpeed * (elapsed / 16);
          }
        } else {
          if (current >= maxScrollLeft - 1) scrollContainer.scrollLeft = 0;
          else scrollContainer.scrollLeft += scrollSpeed * (elapsed / 16);
        }
      }

      lastTimestamp = timestamp;
      animationFrameId = requestAnimationFrame(step);
    };

    const pauseAutoScroll = () => { isPaused = true; };
    const resumeAutoScroll = () => { isPaused = false; };

    scrollContainer.addEventListener('mouseenter', pauseAutoScroll);
    scrollContainer.addEventListener('mouseleave', resumeAutoScroll);
    scrollContainer.addEventListener('touchstart', pauseAutoScroll);
    scrollContainer.addEventListener('touchend', resumeAutoScroll);

    animationFrameId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(animationFrameId);
      scrollContainer.removeEventListener('mouseenter', pauseAutoScroll);
      scrollContainer.removeEventListener('mouseleave', resumeAutoScroll);
      scrollContainer.removeEventListener('touchstart', pauseAutoScroll);
      scrollContainer.removeEventListener('touchend', resumeAutoScroll);
    };
  }, [isRTL, isMobile]);

  useEffect(() => {
    AOS.init({ duration: 1200 });
  }, []);

  const textStyle = {
    fontSize: isMobile ? '1.8rem' : '2rem',
    color: '#fff',
    textShadow:
      '0 0 5px rgb(0, 162, 255), 0 0 10px rgb(36, 60, 196), 0 0 20px rgb(34, 130, 185), 0 0 40px rgb(28, 86, 161), 0 0 80px rgb(214, 187, 197)',
    animation: 'glow 1.5s infinite alternate',
    fontFamily,
  };

  const cards = useMemo(() => [
    { id: 1, title: t("N1"), description: t("ND1"), image: '/images/News1.jpeg', date: 'Wednesday, 19 March 2025' },
    { id: 2, title: t("N2"), description: t("ND2"), image: '/images/News2.jpeg', date: 'Wednesday, 12 February 2025' },
    { id: 3, title: t("N3"), description: t("ND3"), image: '/images/News3.jpeg', date: 'Wednesday, 12 February 2025' },
    { id: 4, title: t("N4"), description: t("ND4"), image: '/images/News5.jpeg', date: 'Wednesday, 12 February 2025' },
    { id: 5, title: t("N5"), description: t("ND5"), image: '/images/News6.jpeg', date: 'Wednesday, 12 February 2025' },
    { id: 6, title: t("N6"), description: t("ND6"), image: '/images/News7.jpeg', date: 'Wednesday, 12 February 2025' }
  ], [t]);

  return (
    <Box pb={4} sx={{ fontFamily }}>
      <style>
        {`
          @keyframes glow {
            0% { text-shadow: 0 0 5px rgb(0, 153, 255), 0 0 10px #0C2182, 0 0 20px #0C2182; }
            100% { text-shadow: 0 0 10px #0C2182, 0 0 20px #0C2182, 0 0 40px #0C2182; }
          }
        `}
      </style>

      <Box sx={{ pt: 5, pl: 5, pr: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', paddingTop: "70px" }}>
          <Box sx={{
            width: 0, height: 0, margin: "5px",
            borderTop: '20px solid transparent',
            borderBottom: '20px solid transparent',
            borderLeft: `20px solid ${isRTL ? 'transparent' : '#0C2182'}`,
            borderRight: `20px solid ${isRTL ? '#0C2182' : 'transparent'}`,
            mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0,
          }} />
          <Typography component="h3" style={{ whiteSpace: 'nowrap', ...textStyle }}>
            {t("NewsCompany")}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable News Cards */}
      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          cursor: 'grab',
          padding: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          direction: isRTL ? 'rtl' : 'ltr',
        }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <Grid container spacing={2} sx={{ flexWrap: 'nowrap', width: 'fit-content' }}>
          {cards.map((card) => (
            <Grid item key={card.id} xs={12} sm={6} md={4} lg={3} xl={2.5}>
              <Card
                onClick={() => handleOpen(card)}
                sx={{
                  fontFamily,
                  width: { xs: '90vw', sm: 400, md: 500 },
                  height: { xs: 'auto', md: 460 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: 'white',
                  borderRadius: 4,
               
                  flex: '0 0 auto',
                  overflow: 'hidden',
                  boxShadow: 8,
                  cursor: 'pointer',
                  direction: isRTL ? 'rtl' : 'ltr',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontSize={{ xs: '1rem', sm: '1.25rem' }} fontWeight={600} mb={1} sx={{ fontFamily }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" fontSize={{ xs: '0.85rem', sm: '0.95rem' }} color="text.secondary" mb={2} sx={{
                    fontFamily,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {card.description}
                  </Typography>
                  <Box sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                    <Box
                      component="img"
                      src={card.image}
                      alt="news"
                      sx={{
                        width: '100%',
                        height: { xs: 180, sm: 200 },
                        objectFit: 'cover',
                        borderRadius: 2,
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        left: isRTL ? 'auto' : 10,
                        right: isRTL ? 10 : 'auto',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '4px 12px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        boxShadow: 1,
                      }}
                    >
                      {card.date}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: 24,
          p: { xs: 2, sm: 4 },
          outline: 'none',
          fontFamily,
          direction: isRTL ? 'rtl' : 'ltr',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedCard && (
            <>
              <Typography variant="h6" fontWeight={600} mb={2} fontSize={{ xs: '1.2rem', sm: '1.5rem' }} sx={{ fontFamily }}>
                {selectedCard.title}
              </Typography>
              <Box
                component="img"
                src={selectedCard.image}
                alt="modal"
                sx={{
                  width: '100%',
                  height: { xs: 180, sm: 250 },
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: 2,
                }}
              />
              <Typography variant="body1" fontSize={{ xs: '0.95rem', sm: '1rem' }} mb={2} sx={{ fontFamily }}>
                {selectedCard.description}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                {selectedCard.date}
              </Typography>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default LatestNews;
